import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TooltipItem } from "chart.js";
import { AnnotationOptions } from "chartjs-plugin-annotation";
import { round, uniqueId } from "lodash-es";
import { binDBKeys } from "src/pages/binOverview/BinCommander";
import BinApiService from "src/api/BinApiService";
import { useMemo } from "react";
import FanOffReason from "src/consts/FanOffReason";
import HeaterOffReason from "src/consts/HeaterOffReason";
import { Dayjs } from "dayjs";
import FanHeaterChangeTimeDatapointDTO from "src/models/FanHeaterChangeTimeDatapointDTO";
import { queryOptions } from "src/queries/v4QueryOptions/queryOptions";

export const compressorColor = "#583381";
export const fanStatusColor = compressorColor
export const heaterStatusColor = "#8f51cc";



export const tooltipItemRound = (tooltipItem: TooltipItem<'line'>) => {
    let label = tooltipItem.dataset.label ?? "";

    if (label) {
        label += ': ';
    }

    if (tooltipItem.dataset.label?.includes("Status")) {

        if (typeof (tooltipItem.raw as any)?.y === 'string') {

            // assumed category
            label += (tooltipItem.raw as any)?.y;
            return label;
        }
    }

    label += round(tooltipItem.parsed.y, 1)?.toFixed(1);
    return label;
  }




  enum FanState {
    Off,
    On,
    Paused
  }
  
  enum HeaterState {
    Off,
    On,
    Paused,
  }

  export const fanOnBackgroundColor = 'rgb(196, 255, 191)';
  export const FanPausedBackgroundColor = 'rgb(245, 245, 245)';
  
  export const heaterOnBackgroundColor = '#fad9bb';
  
  interface FanHeaterAnnotationOptions {
    yAxes?: string[]
  }

  enum FanHeaterChangeType {
    Fan,
    Heater,
  }
  
  export enum FanHeaterStatus {
    Off,
    Paused,
    On,
  }

  export interface ChangeInfo {
    timetamp: string,
    fanStatus: FanHeaterStatus | null,
    prevFanStatus: FanHeaterStatus | null,
    heaterStatus: FanHeaterStatus | null,
    prevHeaterStatus: FanHeaterStatus | null,
    nextCaptureTimeUTC: string | null,
  }

  export function generateFanHeaterChangeAnnotationInfo(data: Array<FanHeaterChangeTimeDatapointDTO>): Array<ChangeInfo> {
    if (data == null) {
      return [];
    }

    const isFanOn = (fanOffReasonId: FanOffReason | null) => {
      if (fanOffReasonId == null) {
        return FanHeaterStatus.On;
      }
      if ([FanOffReason.FanTimer, FanOffReason.KeepFansOffOverride].includes(fanOffReasonId)) {
        return FanHeaterStatus.Off;
      }
  
      return FanHeaterStatus.Paused;
    };

    const isHeaterOn = (heaterOffReasonId: HeaterOffReason | null) => {
      if (heaterOffReasonId == null) {
        return FanHeaterStatus.On;
      }
      return FanHeaterStatus.Off;
    };

    const annotations = data.flatMap(datapoint => {
      const fanstate = isFanOn(datapoint.fanOffReasonId);
      const heaterState = isHeaterOn(datapoint.heaterOffReasonId);
      const prevFanStatus = isFanOn(datapoint.previousFanOffReasonId);
      const prevHeaterStatus = isHeaterOn(datapoint.previousHeaterOffReasonId);

      const result: ChangeInfo = {
        timetamp: datapoint.x,
        fanStatus: fanstate,
        prevHeaterStatus: prevHeaterStatus,
        heaterStatus: heaterState,
        prevFanStatus: prevFanStatus,
        nextCaptureTimeUTC: datapoint.nextCaptureTimeUTC,
      };
      return result;


    });
    return annotations;

  }

  export const useFanHeaterChangeHistoryQueryOptions = (binId: number, startDate: Dayjs | null | undefined, endDate: Dayjs  | null | undefined) => {

    const queryClient = useQueryClient();
    return queryOptions({
      queryKey: [...binDBKeys.bin(binId), "charts", "getFanHeaterChangeHistory", { startDate: startDate, endDate: endDate }],
      queryFn: async (q) => {
  
        const result = await BinApiService.getFanHeaterChangeHistory(binId, startDate?.toISOString()!, endDate?.toISOString()!, null, q.signal);
        queryClient.removeQueries({ queryKey: q.queryKey.slice(0, -1), type: "inactive" });
        return result;
      },
      refetchInterval: Infinity, retry: false, keepPreviousData: true,
      enabled: (binId ?? 0) > 0 && startDate != null && endDate != null,
    });
  }
  
  export const useFanHeaterChangeHistory = (binId: number, startDate: Dayjs | null | undefined, endDate: Dayjs  | null | undefined, options?: FanHeaterAnnotationOptions) => {

    const queryClient = useQueryClient();

    const yAxes = useMemo(() => {
      return options?.yAxes ?? ['y'];
    }, [options?.yAxes])

    const changeQueryOptions = useFanHeaterChangeHistoryQueryOptions(binId, startDate, endDate);

    const chartQuery = useQuery({...changeQueryOptions});
  
    const annotations = useMemo(() => {

      const annotationsInfo = generateFanHeaterChangeAnnotationInfo(chartQuery.data ?? []);
      
      const fanHeaterAnnotations = annotationsInfo?.flatMap((datapoint, index) => {
  
 

        
  
        const statusAnnotations: AnnotationOptions[] = [];
  
        const fanStatus = datapoint.fanStatus;
        const heaterStatus = datapoint.heaterStatus;
        if (fanStatus == FanHeaterStatus.On) {
          const annotation: AnnotationOptions = {
            type: 'box',
            xScaleID: 'x',
            id: uniqueId('fanChange-On-'),
            xMin: datapoint.timetamp,
            xMax: datapoint.nextCaptureTimeUTC as string ?? undefined,
            backgroundColor: fanOnBackgroundColor,
            borderWidth: 0,
            z: -2,
            drawTime: 'beforeDatasetsDraw',        
          };
          for (const yaxis of yAxes) {
            statusAnnotations.push({...annotation, yScaleID: yaxis});
          }
        }
  
        else if (fanStatus == FanHeaterStatus.Paused) {
          const annotation: AnnotationOptions = {
            type: 'box',
            xScaleID: 'x',
            id: uniqueId('fanChange-Paused-'),
            xMin: datapoint.timetamp as string,
            xMax: datapoint.nextCaptureTimeUTC as string ?? undefined,
            backgroundColor: FanPausedBackgroundColor,
            borderWidth: 0,
            z: -3,
            drawTime: 'beforeDatasetsDraw',        
          };
          for (const yaxis of yAxes) {
            statusAnnotations.push({...annotation, yScaleID: yaxis});
          }
        }
  
        if (heaterStatus == FanHeaterStatus.On) {
          const annotation: AnnotationOptions & Record<string, any> = {
            type: 'box',
            id: uniqueId('heaterChange-'),
            xMin: datapoint.timetamp as string,
            xMax: datapoint.nextCaptureTimeUTC as string ?? undefined,            
            //backgroundColor: 'rgb(250, 217, 187)',
            backgroundColor: heaterOnBackgroundColor,
            borderWidth: 0,
            drawTime: 'beforeDatasetsDraw',
            z: -2,
          };
          for (const yaxis of yAxes) {
            statusAnnotations.push({...annotation, yScaleID: yaxis});
          }
        }
  
        return statusAnnotations;
      }) ?? [];
      return fanHeaterAnnotations;
    }, [chartQuery.data, yAxes]);
  
    return {data: chartQuery.data, annotations: annotations};
  }

  export const formatCompressorStatus = (value: number) => {
      if (value === 0) {
        return "OFF";
    }
    if (value === 1) {
        return "ON";
    }
    return '';
  }

  export interface FanControlMoisturePreferenceProps {
    useRHForFanControl: boolean,
  }